import type { Level, OrgKind, Role } from '../constants';
import type { LaneAttribute } from '../lane-attributes';
import type { Actor } from '../seats';
import type { MeansPair } from '../stage-gates';
import type { Command, DomainError, WorkItemState } from '../types';
import type { Message } from '@/i18n';

/**
 * **The shapes the resolver's steps pass each other** (docs/RESOLUTION.md §1).
 * Types only, plus `PLAN_MAX_ITEMS`, in the paper's own order: the stimulus
 * (step 1), the context (step 2), and the intent, question and plan (steps 3,
 * 4 and 6).
 *
 * This file is the bottom of the directory: it imports nothing from it, and
 * every step file reads its shapes from here rather than from the step that
 * happens to build them. That is what keeps the seam one-directional.
 */

// --- Stimulus (§1 step 1) ----------------------------------------------------

/** The edges an `edge` stimulus names: every edge of the table but Cancel,
 *  which keeps its own stimulus kind. */
export type StimulusEdge = 'done' | 'ready' | 'accept' | 'deny' | 'reopen';

/** Where the stimulus came from. The surface is an ARGUMENT (§5), never a code
 *  path; the source says who is waiting, which `mode` then answers for (§3). */
export type StimulusSource = 'person' | 'ticker' | 'agent' | 'queue';

/** What every stimulus carries, whatever it is (§1 step 1). */
export interface StimulusHead {
  /** The idempotency key. One gesture, one key; a replay carries the same one
   *  (docs/MOBILE.md §8, docs/SYNC.md §7.4) and the executor refuses a
   *  duplicate rather than appending twice. */
  key: string;
  source: StimulusSource;
  /** The surface or view the gesture came from ('checklist', 'item-page',
   *  'lane', 'mcp', ...). An argument, so no surface holds meaning of its own. */
  surface: string;
  /** The device time of the act, ISO-8601 (docs/MOBILE.md §8). Absent for a
   *  live act, which takes server time from the column default. */
  deviceTime?: string;
}

/** What an `act` stimulus points at: a verb picked from an act sheet, or a
 *  value picked on the line / dropped on a lane. (`LaneAttribute` already
 *  includes `verdict` - the four laneable axes of docs/VIEWS.md §3a.) */
export type ActTarget = { command: Command } | { attribute: LaneAttribute; value: string };

export type Stimulus =
  // A tick: "I am done" (§2). Always true, never asked, never refused for the
  // item's sake.
  | (StimulusHead & { kind: 'tick'; itemId: string })
  // "Take that statement back" - the inverse of the plan the origin names (§2):
  // the newest TICK plan on the item, or the plan `originKey` names (a surface
  // offering the undo of its own statement, e.g. the retag dialog).
  | (StimulusHead & { kind: 'untick'; itemId: string; originKey?: string })
  | (StimulusHead & { kind: 'act'; itemId: string; target: ActTarget })
  // The way out (docs/CLOSED_AXIS.md): conclude with the delivery taken back,
  // so the item reads CANCELLED rather than done. Two pairs, so it is its own
  // row rather than an `act` target - and a row here is the whole point (the
  // surface used to compose Retract-then-Close by hand).
  // The optional `reason` rides the Close the pairs compile to, exactly as a
  // plain `Close` carries one: a cancellation IS a conclusion, and a conclusion
  // may say why. The item page's kebab sends none; the MCP's `item_act`
  // cancel does (2026-09-21).
  | (StimulusHead & { kind: 'cancel'; itemId: string; reason?: string })
  // One edge of the finishing lifecycle (docs/CLOSED_AXIS.md "Finishing",
  // Leon 2026-11-03): the edge table (`domain/lifecycle-edges.ts`) says
  // which edges stand here, `resolveEdge` what each one does. Cancel keeps
  // its own kind above. `note` rides an Accept, `reason` a Deny.
  | (StimulusHead & {
      kind: 'edge';
      itemId: string;
      edge: StimulusEdge;
      note?: string;
      reason?: string;
    })
  | (StimulusHead & { kind: 'move'; itemId: string; flowId: string; toStageKey: string })
  // A title typed with `#word` tokens (docs/FIELDS_AND_TAGS.md §6.3): the
  // stripped title and one tag per token, one statement. A title with no
  // token stays a plain `SetTitle` outside the resolver (see resolveRetitle).
  | (StimulusHead & { kind: 'retitle'; itemId: string; text: string })
  // One worn tag edited into another on this item (docs/FIELDS_AND_TAGS.md
  // §6.3: the chip opens its own path, e.g. `einkaufen` -> `einkaufen/obst`).
  // An Untag and a Tag, one statement, one plan, so one undo takes both back.
  | (StimulusHead & { kind: 'retag'; itemId: string; from: string; to: string });

/**
 * A stimulus with everything but its KEY (§1 step 1). A menu row carries one of
 * these: what the row MEANS is decided when the rows are built, the gesture's
 * key is minted at the tap, and the plan the surface then previews is the plan
 * the server is asked to execute (the determinism check hashes the key, so the
 * preview and the act must share it).
 */
export type PendingStimulus =
  | { kind: 'tick' }
  | { kind: 'untick'; originKey?: string }
  | { kind: 'act'; target: ActTarget }
  | { kind: 'cancel'; reason?: string }
  | { kind: 'edge'; edge: StimulusEdge; note?: string; reason?: string }
  | { kind: 'move'; flowId: string; toStageKey: string }
  | { kind: 'retitle'; text: string }
  | { kind: 'retag'; from: string; to: string };

/**
 * The origin marker a plan stamps on every event it appends (§8): the plan's
 * id, its one sentence, and the stimulus key behind it. It is what lets the
 * feed fold three mechanics into one statement, an un-tick find its inverse,
 * and an event-stimulus name the plan that produced it.
 */
export interface Origin {
  planId: string;
  /** The plan's one sentence as a descriptor (docs/I18N.md): the log stores
   *  the key and its arguments, never English. Rows written before 2026-10-03
   *  hold a string; the reader turns it back into a descriptor
   *  (`parseLegacyStatement`), so this type only ever holds the descriptor. */
  statement: Message;
  stimulusKey: string;
  /** What the plan answered: the stimulus kind (`retag` covers both a
   *  single item's edit and the multi-item tag rename). It is how an un-tick finds the newest TICK plan and skips a later
   *  tag or retitle plan (`untickOrigin`). Absent on plans written before
   *  2026-09-29. */
  stimulusKind?: OriginKind;
  /** On an un-tick plan: the stimulus key of the plan it took back. It is how
   *  a later un-tick tells a tick already taken back from a tick under a
   *  named undo of another plan (`untickOrigin`). Absent on un-tick plans
   *  written before 2026-10-18, and on every other kind. */
  undone?: string;
}

/** An origin marker as the log holds it: rows written before 2026-10-03
 *  carry the statement as an English sentence. `readOrigin` turns either
 *  shape into an `Origin`. */
export type StoredOrigin = Omit<Origin, 'statement'> & { statement: Message | string };

/** The kinds of plan an origin marker names: every stimulus kind. `retag` is
 *  both the item-scoped row here and the multi-item tag rename, which runs its
 *  own plan outside `resolve` (`src/server/retag.ts`) under the same word. */
export type OriginKind = Stimulus['kind'];

// --- Context (§1 step 2) -----------------------------------------------------

/** Who waits, and therefore what a question may do (§3). */
export type Mode = 'interactive' | 'async' | 'agentic';

/** The question classes a rule may hit, and a policy is written per class (§3).
 *  `locked-child` was retired on 2026-09-21 with the second conclusion
 *  question: a child that is not the actor's to conclude is skipped and
 *  counted, never asked about again. */
export type QuestionClass = 'open-children' | 'cascade-size' | 'out-of-role';

/** What an async caller does with a question it cannot wait for (§3): answer
 *  it as the rule's author pre-answered it, raise it as a proposal, or skip
 *  (which is a finding, never a human in the loop per firing). */
export type Policy = { answer: string } | 'propose' | 'skip';

/**
 * What a ticked line still waits on (§2: "what the person must be able to see
 * is exactly one thing"). Name-free, like every statement the domain makes -
 * it speaks seats and counts, and a surface that holds a directory may render
 * a seat holder's name.
 */
export type Waiting = { on: 'review' } | { on: 'requester'; refs: readonly string[] };

/** A view-authored tick meaning (§5, direction - not settled). A lens may say
 *  its tick delivers, accepts or closes; with none, §2's table decides. */
export type TickMeans = 'deliver' | 'accept' | 'close';

/**
 * The access facts step 5 decides on, in one of two forms.
 *
 * The substrates that hold the whole graph gather the FACTS: the resolved role
 * on the item (grants only - seats are contract facts, HS-F9), the org's kind,
 * whether the viewer stands in that org. A caller that already holds the
 * ANSWER passes it as `standing` instead: the read layer grades every row it
 * hands a surface (the sight predicate's grade: `act` or `view`), and a client
 * running this resolver optimistically (§10, the strike waits on nothing) has
 * that grade and not the four facts behind it. Either way step 5 asks one
 * question, and `mayAct` stays its single definition.
 *
 * `ownerReach` is on both forms: it is a contract party fact (the requester
 * seat, `domain/owner-reach.ts`), not access, and it guards the one reserved
 * act.
 */
export type AccessContext =
  | {
      standing?: undefined;
      role: Role | null;
      orgKind: OrgKind;
      memberOfAnyGroup: boolean;
      /** Whether the actor's direct paths count (docs/AGENT_ACCESS.md §4). */
      own: boolean;
      ownerReach: boolean;
    }
  | {
      /** The already-resolved answer to "may this actor act here?". */
      standing: 'act' | 'view';
      ownerReach: boolean;
    };

/**
 * The access facts the item page holds (`itemActFacts`, src/read): the
 * answered form of `AccessContext`, beside it `memberReach`, the bar a board's
 * root asks (`hasMemberReach`, domain/owner-reach.ts), so the page offers a
 * board of the item's sub-items only where the create would pass. The
 * resolver reads none of `memberReach`: it rides the same facts because the
 * page resolves both reaches in one read.
 */
export type ItemAccessFacts = Extract<AccessContext, { standing: 'act' | 'view' }> & {
  memberReach: boolean;
};

/** One flow the item belongs to, with the stage a `move` targets and the gate
 *  verdict for it. The verdict comes from `decideAdvanceMember`, evaluated by
 *  the context builder against its own substrate (§9's two data sources), so
 *  the gate rule lives in one place and the resolver only reads it. */
export interface MembershipContext {
  flowId: string;
  flowName?: string;
  /** The member's current position. */
  stageKey: string;
  stageLabel: string;
  /** The stage the stimulus targets, with the `means` placing the item there
   *  applies (docs/FLOW_DEFINITION.md §11). */
  target?: { key: string; label: string; means: readonly MeansPair[] };
  gate: { allows: boolean; reason?: Message; level?: Level };
}

/**
 * One open item of the target's real subtree, for the sweep (§6). `depth` is
 * how far below the target it sits (1 = a direct child).
 *
 * The three facts beside the state are what the sweep needs to SKIP a child
 * that has a life outside the concluded set (ruled 2026-09-21): it is not the
 * actor's to conclude, another open parent lists it, or an open item depends on
 * it. Each is a COUNT and never a list of ids: the tie sits on items the actor
 * may not see, and a count of edges on their own item is no oracle.
 */
export interface ChildContext {
  itemId: string;
  depth: number;
  state: WorkItemState;
  /** `mayAct` resolved for this actor on this child. */
  canAct: boolean;
  /** Active parent edges to OPEN items OUTSIDE the subtree: the child is
   *  listed somewhere that carries on after this conclusion. */
  outsideParents: number;
  /** OPEN items outside the subtree that are blocked by this child. */
  outsideDependents: number;
  /** This child's open parents WITHIN the subtree that the actor can open, by
   *  id (the target included). A skip travels downward: a child that stays
   *  open keeps its own work, so a descendant reachable only through a staying
   *  item stays too. */
  parentsInSubtree: readonly string[];
  /** This child's open parents WITHIN the subtree that the actor sees only as
   *  stubs. Such a parent is never concluded by this sweep, so the child stays
   *  open with it. */
  stubParentsInSubtree: readonly ParentStub[];
}

/**
 * An open parent the actor cannot open, as its stub (docs/MODEL.md
 * "Discoverability and request-access"): the active parent edge to a child the
 * actor can open admits its id and its result, in the shape a relationship row
 * gives a stub (`EndpointStub`, src/read/access.ts). Nothing else of it is
 * carried: no title, no seats, no atom beyond the result. Only an open parent
 * holds a child, so the result is always open.
 */
export interface ParentStub {
  id: string;
  stub: { result: 'open' };
}

/** The counts that ride the row (§10): what is still open below, and what this
 *  item still waits on. The subtree itself is gathered only when a plan needs
 *  it (`children`). */
export interface Weight {
  openChildren: number;
  openBlockers: number;
}

/** An answer already given to a question this pipeline asked (§4). An answer
 *  is part of the context and the pipeline runs again; it is never a release
 *  of a stored plan. */
export interface Answer {
  questionId: string;
  optionKey: string;
  /**
   * The children this answer concludes, by id - §6's composer (ruled by Leon
   * 2026-09-22): *"you can select any of the three presets and then tick boxes
   * on and off"*. A preset's own set, or the set a person composed off one;
   * absent means "the set the option named by `optionKey` computes". The pure
   * layer has ONE code path either way: a selection in, a cascade out.
   *
   * It never widens what the machine permits: an id that is not a visible,
   * concludable child of this gesture's target refuses the whole gesture.
   */
  selection?: readonly string[];
}

export interface Context {
  item: WorkItemState;
  actor: Actor;
  access: AccessContext;
  memberships: readonly MembershipContext[];
  weight: Weight;
  /** The open real subtree, deepest first, as the VIEWER sees it. Present only
   *  when the stimulus could conclude the item and something is open below. */
  children?: readonly ChildContext[];
  /** Open descendants the viewer may NOT see, counted with `children` and
   *  never apart from it. They are never planned and never named; they are
   *  part of what stays open. */
  hiddenChildren?: number;
  mode: Mode;
  /** Per question class, what an async caller does with it (§3). */
  policies?: Partial<Record<QuestionClass, Policy>>;
  answers: readonly Answer[];
  /** The view's grammar (§5). */
  grammar?: { tickMeans?: TickMeans };
  /**
   * Whether a review duty stands (docs/CLOSED_AXIS.md "Finishing"): a flow
   * stage that means `verdict: accepted`, or the item's own Review required
   * setting. Ruled by Leon 2026-09-21: *"skipping gates that were willfully
   * applied should hold. If you don't want that, remove the gate first and
   * then close."* Under it Done is not offered and the forward edge of open
   * is Ready for review.
   */
  reviewRequired: boolean;
  /** The refs a flow's review stage names as its gatekeeper: reviewers beside
   *  the requester seat (the edge table's `reviewer`). */
  reviewers: readonly string[];
  /** A seat on the item is offered to someone, so the item has a party to
   *  come besides the actor (the edge table's `onlyParty`). */
  seatOffered: boolean;
  /** A flow's stage `means` apply `delivered`: the stage move hands the work
   *  in (the edge table's `deliveryCovered`). */
  deliveryCovered: boolean;
  /** For an un-tick and an event stimulus: the plan being reversed, or the one
   *  the event came out of. */
  origin?: Origin;
  /** The steps of the plan `origin` names, recovered from the log (the events
   *  carrying that plan id, in `seq` order, across every item it touched). An
   *  un-tick inverts exactly these; absent means the origin could not be
   *  recovered. */
  originSteps?: readonly PlanStep[];
  /** The OTHER items that plan touched - a tick's sweep (§6) - with the state
   *  they are in now and whether the actor may still act on them. An un-tick
   *  folds its inverse against these; an item missing from them, or one the
   *  actor may no longer act on, refuses the whole reversal rather than
   *  taking back half a statement. */
  originItems?: readonly OriginItem[];
}

/** One item besides the target that the recovered origin plan touched (§8). */
export interface OriginItem {
  itemId: string;
  state: WorkItemState;
  /** `mayAct` resolved for this actor on this item, now. */
  canAct: boolean;
}

// --- Intent, plan, question (§1 steps 3, 4, 6) -------------------------------

export interface PlanStep {
  itemId: string;
  command: Command;
  /** The accountability level `decide` graded this step at (Prinzip 20):
   *  absent = routine, `warning` out-of-role, `critical` self-dealing. */
  level?: Level;
}

/**
 * One answer a question offers. §6's conclusion offers three - `leave`, the
 * safe `sweep`, and `force` - and each is a PRESET: a name for the set it
 * would conclude, which a person may then tick on and off (`Answer.selection`).
 *
 * **A row that would do exactly what another row does must not render**
 * (Leon, 2026-09-22), so an option appears only where its set differs from
 * every other's.
 */
export interface QuestionOption {
  key: string;
  label: Message;
  /**
   * The one sub-line: what the answer does that its verb does not name
   * (docs/DESIGN_SYSTEM.md, "a sub-line only where the act costs more than its
   * word says"). It names what stays and WHY - by title where the viewer can
   * see it, by count where they cannot - and the two facts stay apart: work
   * other open items still need, versus work that is not theirs to conclude.
   */
  detail?: Message;
  /** This answer concludes work other open items still need: the danger
   *  register, because the cost is outside the item being closed. */
  danger?: boolean;
  /** The children this preset would conclude, by id. Absent where the subtree
   *  was not gathered (a surface that can speak the question but not sweep). */
  selection?: readonly string[];
}

/**
 * One open descendant the conclusion LISTS (§6's composer, 2026-09-22). A
 * child outside the actor's sight is never here: it stays a count in a clause,
 * for the same reason it is never named.
 */
export interface QuestionSubject {
  itemId: string;
  title: string;
  /** How far below the target it sits (1 = a direct child). */
  depth: number;
  /** Whether the actor may conclude it. A locked subject is SHOWN and never
   *  selectable - neither force nor a checkbox unlocks it. */
  selectable: boolean;
  /** Why it is locked, in the SEAT's words, never a person's
   *  (docs/DESIGN_SYSTEM.md, "a locked row wears the seat"). */
  lock?: Message;
  /** Why the safe preset leaves it, and force takes it: another open list
   *  holds it, or open work outside this item depends on it. */
  tie?: 'listed' | 'needed';
}

export interface Question {
  id: string;
  /** `clarification` (§1 step 3: which meaning) or `confirmation` (§1 step 6:
   *  this is what will happen, yes?). */
  kind: 'clarification' | 'confirmation';
  class: QuestionClass;
  text: Message;
  options: QuestionOption[];
  /** The actor refs that may answer (§4). Never empty: a question with nobody
   *  to ask IS the vacancy fact, and the plan is refused whole instead. */
  answerers: string[];
  /**
   * What the question is ABOUT, one entry per thing the answer covers (§6's
   * composer): the open descendants the viewer can see, so a surface can list
   * them with a box each and hand back the set. Absent where the question has
   * no per-subject shape, or where the subtree was not gathered.
   */
  subjects?: readonly QuestionSubject[];
}

/** Which row of the table the stimulus landed on. The SUBSTANCE is the
 *  statement and the commands; this word only names the row. */
export type IntentKind =
  | 'tick'
  | 'untick'
  | 'attribute'
  | 'command'
  | 'edge'
  | 'cancel'
  | 'move'
  | 'retitle'
  | 'retag';

/**
 * Step 4: the one statement, plus what it compiles to before the fold and
 * before permissions. A lane carries the intent (a proposal may carry an act,
 * §1 step 5), and a refusal carries it when one was formed at all.
 */
export interface Intent {
  kind: IntentKind;
  itemId: string;
  /** The one sentence: what the surface shows, what the feed speaks, and what
   *  the log records as the plan's origin. Name-free by construction - the
   *  domain holds refs, never PII, so it speaks seats ("Seated as doer;
   *  delivered and closed.") and a surface that knows names may say them.
   *  A descriptor, never English: the surface formats it in the viewer's
   *  locale (docs/I18N.md). */
  statement: Message;
  commands: readonly Command[];
  transition?: { flowId: string; toStageKey: string };
  /** What the item still waits on once this intent has landed, for the ticked
   *  line's quiet trailing note (§2). Absent = nothing waits. */
  waits?: Waiting;
  /** The subtree this intent also covers (§6's cascade), in execution order. */
  cascade?: readonly { itemId: string; commands: readonly Command[] }[];
}

export interface Plan {
  /** Deterministic: a stable hash of the stimulus key and everything the plan
   *  would do (its steps and its transition). Two runs of the same
   *  stimulus against the same truth produce the same id - which is what the
   *  executor's determinism check compares (§1 step 7). */
  id: string;
  statement: Message;
  intent: Intent;
  /** Ordered, folded through `decide` against the evolving state. */
  steps: PlanStep[];
  transition?: { flowId: string; toStageKey: string };
  /** The intent's `waits`, carried so a surface renders the note from the plan
   *  it was handed (§2). Derived from the same inputs as the steps, so it is
   *  deliberately not part of the plan id. */
  waits?: Waiting;
  consequences: Message[];
  /** Step 6's "this is what will happen, yes?". A plan that still carries an
   *  unanswered confirmation is returned, never executed. */
  confirm?: Question;
}

/**
 * What the pipeline returns. A lane and a refusal that an async POLICY decided
 * (§3) name the question class they came from, so the caller that raises the
 * proposal or records the finding can say which question the rule hit - the
 * author's signal - without parsing a sentence.
 */
export type Resolution =
  | { kind: 'plan'; plan: Plan }
  | { kind: 'question'; question: Question }
  | {
      kind: 'lane';
      lane: 'propose' | 'request-access';
      intent: Intent;
      reason: Message;
      questionClass?: QuestionClass;
    }
  | { kind: 'refused'; error: DomainError; intent?: Intent; questionClass?: QuestionClass };

/** A plan over more than this many items is refused: "too large for one
 *  gesture; close the sub-stories first" (§6). */
export const PLAN_MAX_ITEMS = 200;
